import {
  BATTERY_BREAK_POINTS,
  BATTERY_LEVELS,
  FIRST_WEIGHT_TO_LOSE,
  HEALTY_IMC,
  IMC_BREAKPOINTS,
  IMC_MAX_ROTATION,
  IMC_NORMALITY_MAX_VALUE,
  IMC_OBESITY_MAX_VALUE,
  IMC_OVERWEIGHT_MAX_VALUE,
  IMC_ROTATION_BREAKPOINTS,
  NORMALITY_COLOR,
  NORMALITY_DARK_COLOR,
  OBESITY_COLOR,
  OBESITY_DARK_COLOR,
  OVERWEIGHT_COLOR,
  OVERWEIGHT_DARK_COLOR,
  SCORES_BREAK_POINTS_COLORS,
  SCORES_COLORS,
  SCORES_LABELS,
  SCORES_VALUES,
  TOOLTIP_COLOR,
  WHITE_COLOR,
} from "@middleware/constants";
import {
  ETooltipPosition,
  TBilanData,
  TCoordonates,
  TKeyValueMap,
  TScoreData,
  TTooltipPosition,
} from "@middleware/types";

export const getScoreInfo = (score: number): TScoreData => {
  const ScoreValues = SCORES_VALUES;
  const ScoreColors = SCORES_COLORS;
  const GraphLabels = SCORES_LABELS;
  const ScoreData: TScoreData = {};
  let sum = 0;

  for (let index = 0; index < ScoreValues.length; index++) {
    sum += ScoreValues[index];
    if (score <= sum) {
      ScoreData.color = ScoreColors[index];
      ScoreData.label = GraphLabels[index];

      break;
    }
  }

  return ScoreData;
};

export const getHorizontalAxisDegrees = (data: TBilanData): number => {
  const difference = data.currentWeight - data.desiredWeight;

  if (difference < 20) return 1;
  if (difference < 40) return 2;
  if (difference < 60) return 3;
  if (difference < 90) return 4;

  return 5;
};

export const getLandMarkXDetails = (
  data: TBilanData,
): { min: number; max: number; step: number } => {
  const imcMax = Math.floor(getIMC(data.currentWeight, data.height));
  const finalWeight =
    data.desiredImc > HEALTY_IMC ? data.desiredWeight : data.healtyWeight;
  let imcMin = Math.ceil(getIMC(finalWeight, data.height));
  const horizontalAxisDegrees = getHorizontalAxisDegrees(data);
  if (imcMax < imcMin) imcMin = imcMax;

  let max, step;
  if (imcMax === imcMin) {
    step = 1;
    max = imcMax + 1;
    imcMin -= step;
  } else {
    step = Math.max(1, Math.ceil((imcMax - imcMin) / horizontalAxisDegrees));
    max = horizontalAxisDegrees >= 5 ? imcMax + step : imcMax;
    while ((imcMax - imcMin) % step !== 0) {
      imcMin--;
    }
  }

  return { max, min: imcMin - step, step };
};

export const getNextMaxImc = (
  max: number,
  min: number,
  step: number,
  degree: number,
): number => {
  const remainder = (max - min) % degree;
  if (max > step * degree) return step * degree + min;

  if (remainder !== 0) return getNextMaxImc(max + step, min, step, degree);

  return max;
};

export const getLandMarkX = (data: TBilanData) => {
  const landMarkXDetails = getLandMarkXDetails(data);
  const step = landMarkXDetails.step;
  const max = landMarkXDetails.max;
  const min = landMarkXDetails.min;

  const landMarkX: number[] = [];

  for (let index = min; index <= max; index += step) {
    landMarkX.push(index);
  }

  return landMarkX;
};

export const getLandMarkY = (data: TBilanData): number[] => {
  const nextNumberMultiplyBy10 = getNextNumberMultiplyBy10(data.currentWeight);
  if (data.currentWeight - data.recommendedWeight <= 0) {
    return [nextNumberMultiplyBy10];
  }
  const diff = data.currentWeight - data.desiredWeight;
  const step =
    diff > 125 ? 100 : diff > 80 ? 50 : diff > 60 ? 30 : diff > 40 ? 20 : 15;
  const landMarkY: number[] = [];

  for (let i = nextNumberMultiplyBy10; i >= data.desiredWeight; i -= step) {
    landMarkY.push(i);
  }

  return landMarkY;
};
export const getNextNumberMultiplyBy10 = (num: number): number => {
  return Math.ceil(num / 10) * 10;
};
export const getIMC = (weight: number, height: number): number => {
  const heightInMeters = height / 100;
  const bmi = weight / Math.pow(heightInMeters, 2);

  return Math.round(bmi);
};

export const getNumberOfObjectives = (data: TBilanData): number[] => {
  if (
    data.recommendedWeight >= data.currentWeight ||
    data.recommendedImc >= data.currentImc
  )
    return [data.currentWeight];

  if (
    data.recommendedWeight === data.desiredWeight ||
    Math.round(data.recommendedImc) === Math.round(data.healthyImc)
  )
    return [data.currentWeight, data.recommendedWeight];

  if (data.desiredImc > HEALTY_IMC)
    return [data.currentWeight, data.recommendedWeight, data.desiredWeight];

  return [data.currentWeight, data.recommendedWeight, data.healtyWeight];
};

export const getObjectivesCoordinates = (
  objectiveWeight: number,
  data: TBilanData,
): TCoordonates => {
  const imc = getIMC(objectiveWeight, data.height);
  const landMarkXDetails = getLandMarkXDetails(data);
  const max = landMarkXDetails.max;
  const min = landMarkXDetails.min;

  let x = (100 * (imc - min)) / (max - min);
  if (x < 0) x = 0;
  if (x > 100) x = 100;

  const y = 0.715 * x + 26; // This is a affine function (Math function)

  return { x, y };
};

export const getObjectiveTooltipPosition = (
  index: number,
  length: number,
  bilanData: TBilanData,
): TTooltipPosition => {
  const { currentWeight, desiredWeight, recommendedImc, currentImc } =
    bilanData;

  const distanceFromSecondToFinalStep =
    currentWeight - FIRST_WEIGHT_TO_LOSE - desiredWeight;

  let positions: TTooltipPosition[] = [];

  if (
    distanceFromSecondToFinalStep > 0 &&
    FIRST_WEIGHT_TO_LOSE > distanceFromSecondToFinalStep
  ) {
    positions = [
      ETooltipPosition.POSITION_TOP,
      ETooltipPosition.POSITION_TOP,
      ETooltipPosition.POSITION_RIGHT,
    ];
  } else if (
    distanceFromSecondToFinalStep > 0 &&
    FIRST_WEIGHT_TO_LOSE <= distanceFromSecondToFinalStep
  ) {
    positions = [
      ETooltipPosition.POSITION_TOP,
      ETooltipPosition.POSITION_RIGHT,
      ETooltipPosition.POSITION_TOP,
    ];
  } else if (Math.round(recommendedImc) === Math.round(currentImc)) {
    positions = [
      ETooltipPosition.POSITION_TOP,
      ETooltipPosition.POSITION_RIGHT,
    ];
  } else {
    positions = [ETooltipPosition.POSITION_TOP, ETooltipPosition.POSITION_TOP];
  }

  return positions[index];
};

export const getObjectiveTooltipColor = (weight: number, height: number) => {
  const imc = getIMC(weight, height);

  if (imc <= IMC_NORMALITY_MAX_VALUE) {
    return NORMALITY_COLOR;
  } else if (imc <= IMC_OVERWEIGHT_MAX_VALUE) {
    return OVERWEIGHT_COLOR;
  }

  return OBESITY_COLOR;
};

export const getGraphBgImage = (data: TBilanData) => {
  const landMarkXDetails = getLandMarkXDetails(data);
  const max = landMarkXDetails.max;
  const min = landMarkXDetails.min;

  const pinkColorPercent = calculateAxisXPercent(
    max,
    min,
    IMC_OVERWEIGHT_MAX_VALUE,
  );

  const orangeColorPercent = calculateAxisXPercent(
    max,
    min,
    IMC_NORMALITY_MAX_VALUE,
  );

  const pinkValue = `0% ${pinkColorPercent - pinkColorPercent / 4}%`;
  const orangeValue = `${pinkColorPercent}% ${
    orangeColorPercent - orangeColorPercent / 4
  }%`;
  const greenValue = `${orangeColorPercent + orangeColorPercent / 4}% 100% `;

  return {
    backgroundImage: `linear-gradient(
          to right,
          ${OBESITY_DARK_COLOR} ${pinkValue},
          ${OVERWEIGHT_DARK_COLOR} ${orangeValue} ,
          ${NORMALITY_DARK_COLOR} ${greenValue}
        )`,
  };
};

export const calculateAxisXPercent = (
  max: number,
  min: number,
  average: number,
) => {
  if (max < average) {
    return 0;
  } else {
    return ((max - average) / (max - min)) * 100;
  }
};

export const replaceVariablesInPrismicField = (
  text: string | null | undefined,
  map: TKeyValueMap,
): string => {
  if (typeof text !== "string" || text === "") return "";

  return Object.entries(map).reduce(
    (result, [key, value]) => result.replaceAll(key, value.toString()),
    text,
  );
};
export const getAxeYCoordinate = (data: TBilanData) => {
  const diff = data.currentWeight - data.recommendedWeight;
  const step =
    diff >= 3
      ? Math.round((diff / 3) * 2)
      : Number(((diff / 3) * 2).toFixed(1));

  return Array.from({ length: 3 }, (_, index) =>
    Number((data.currentWeight - step * index).toFixed(1)),
  );
};

export const getGraphImcValue = (data: TBilanData) => {
  const imc = data.currentImc;
  if (imc > 100) {
    return IMC_MAX_ROTATION;
  }
  const breakpoints = IMC_BREAKPOINTS;
  const rotationBreakpoints = IMC_ROTATION_BREAKPOINTS;

  const index = breakpoints.findIndex((breakpoint) => imc <= breakpoint);
  const percentage =
    ((imc - breakpoints[index - 1]) /
      (breakpoints[index] - breakpoints[index - 1])) *
    100;
  const rotate =
    rotationBreakpoints[index - 1] +
    (percentage / 100) *
      (rotationBreakpoints[index] - rotationBreakpoints[index - 1]);

  return rotate - 180;
};
export const getImcColor = (imc: number) => {
  const colors = SCORES_BREAK_POINTS_COLORS;

  if (imc <= IMC_NORMALITY_MAX_VALUE) {
    return colors[0];
  } else if (imc <= IMC_OVERWEIGHT_MAX_VALUE) {
    return colors[2];
  } else if (imc <= IMC_OBESITY_MAX_VALUE) {
    return colors[4];
  }

  return colors[6];
};
export const getImcTextColor = (imc: number) => {
  if (imc <= IMC_NORMALITY_MAX_VALUE) {
    return WHITE_COLOR;
  }

  return TOOLTIP_COLOR;
};
export const getImcClasses = (imc: number) => {
  if (imc <= IMC_NORMALITY_MAX_VALUE) {
    return "normalTooltip";
  } else if (imc <= IMC_OVERWEIGHT_MAX_VALUE) {
    return "overweightTooltip";
  } else if (imc <= IMC_OBESITY_MAX_VALUE) {
    return "obesityTooltip";
  }

  return "ovesitySevereTooltip";
};

export const getBetteryLevel = (score?: number) => {
  if (score === undefined) return "";

  const levels = BATTERY_LEVELS;
  const breakpoints = BATTERY_BREAK_POINTS;
  const index = breakpoints.findIndex((point) => score > point);

  return levels[index];
};
export const getImcLevel = (levels: string[], imc?: number) => {
  if (imc === undefined) return "";

  const breakpoints = IMC_BREAKPOINTS.slice(1, IMC_BREAKPOINTS.length - 1)
    .sort()
    .reverse();
  const index = breakpoints.findIndex((point) => imc > point);

  return levels[index];
};
